import React from "react";
import {
  MdLocationOn,
  MdLocationCity,
  MdStar,
  MdDeleteOutline,
  MdMeetingRoom,
  MdStairs,
  MdApartment,
  MdPhoneInTalk,
} from "react-icons/md";

import { useSelector } from "react-redux";

import styles from "./AddressCard.module.scss";
import { FullAddress } from "../../../common/types/types";
import { useTheme } from "../../../common/hooks/useTheme";
import { selectIsOnline } from "../../../store/slices/networkSlice";
import Button from "../../../common/components/button/Button";

type AddressCardProps = {
  address: FullAddress;
  deleteAddress: (id: number) => void;
  setDefaultAddress: (id: number) => void;
};

export default function AddressCard({
  address,
  deleteAddress,
  setDefaultAddress,
}: AddressCardProps) {
  const { theme } = useTheme();
  const isOnline = useSelector(selectIsOnline);

  // Проверяем наличие дополнительных полей
  const hasExtraDetails =
    address.entrance || address.floor || address.apartment || address.intercom;

  // Формируем массив деталей для отображения
  const extraDetails = [];
  if (address.apartment)
    extraDetails.push({ icon: MdApartment, text: `кв. ${address.apartment}` });
  if (address.entrance)
    extraDetails.push({
      icon: MdMeetingRoom,
      text: `под. ${address.entrance}`,
    });
  if (address.floor)
    extraDetails.push({ icon: MdStairs, text: `эт. ${address.floor}` });
  if (address.intercom)
    extraDetails.push({
      icon: MdPhoneInTalk,
      text: `домофон ${address.intercom}`,
    });

  return (
    <div
      className={styles.card}
      style={{ backgroundColor: theme.card, borderColor: theme.border }}
    >
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <h3 className={styles.title} style={{ color: theme.text }}>
            {address.name}
          </h3>
          {address.isGeneral && (
            <div
              className={styles.defaultBadge}
              style={{ borderColor: theme.success }}
            >
              <MdStar size={14} color={theme.success} />
              <span
                className={styles.defaultBadgeText}
                style={{ color: theme.success }}
              >
                Основной
              </span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.details}>
        {address.address?.value && (
          <div className={styles.detailRow}>
            <MdLocationOn
              size={16}
              color={theme.primary}
              className={styles.detailIcon}
            />
            <span className={styles.detailText} style={{ color: theme.text }}>
              {address.address.value}
            </span>
          </div>
        )}

        {address.city?.value && (
          <div className={styles.detailRow}>
            <MdLocationCity
              size={16}
              color={theme.primary}
              className={styles.detailIcon}
            />
            <span className={styles.detailText} style={{ color: theme.text }}>
              {address.city.value}
            </span>
          </div>
        )}
      </div>

      {/* Дополнительные детали адреса */}
      {hasExtraDetails && (
        <div className={styles.extraDetails}>
          {extraDetails.map((detail, index) => (
            <div key={index} className={styles.extraDetailItem}>
              <detail.icon size={14} color={theme.primary} />
              <span
                className={styles.extraDetailText}
                style={{ color: theme.text }}
              >
                {detail.text}
              </span>
            </div>
          ))}
        </div>
      )}

      {address.description && (
        <div
          className={styles.comment}
          style={{ borderTopColor: theme.border }}
        >
          <span className={styles.commentLabel}>Комментарий: </span>
          <span
            className={styles.commentText}
            style={{ color: theme.textSecondary }}
          >
            {address.description}
          </span>
        </div>
      )}

      <div className={styles.actions}>
        {!address.isGeneral && (
          <Button
            disabled={!isOnline}
            title="Сделать основным"
            onClick={() => address.id && setDefaultAddress(address.id)}
            variant="outline"
            size="small"
            icon={<MdStar size={16} />}
            style={{ borderColor: theme.warning }}
          />
        )}

        <Button
          title="Удалить"
          onClick={() => address.id && deleteAddress(address.id)}
          disabled={!isOnline}
          variant="outline"
          size="small"
          icon={<MdDeleteOutline size={16} />}
          style={{ borderColor: theme.danger }}
          textStyle={{ color: theme.danger }}
        />
      </div>
    </div>
  );
}
